import React, { useState, useEffect } from 'react';
import { Link, Redirect, useHistory } from 'react-router-dom';
import userStore from '../../../Stores/User';
import pricingStore from '../../../Stores/PricingStore';
import { observer } from 'mobx-react';
import { loadStripe } from '@stripe/stripe-js';
import mixpanel from 'mixpanel-browser';
import loadingIcon from '../../../Assets/loading.gif';
import { validateCoupon, cancelSetupIntent } from '../../../audiblogsApi';
import queryString from 'query-string';
import CheckoutForm from '../../CheckoutForm';
import PricingCards from '../../../Components/PricingCards';
import firebase from "../../../firebase";
import { isMobile, isDesktop, isSafari, isChromium, isAndroid, isIOS, isChrome } from 'react-device-detect';
import Loader from '../../../Components/Loader';
import { COUPON_CONFIGS, PARTNER_CONFIGS, REFERRAL_CONFIGS } from '../../../constants/partners';

const STRIPE_API_KEY = process.env.REACT_APP_STRIPE_API_KEY;
const stripePromise = loadStripe(STRIPE_API_KEY);

// A/B Test helper function
const getPaywallVariant = (params) => {
  // Check for forced variant first
  const forcedVariant = params.paywall?.toLowerCase();
  if (forcedVariant === 'a' || forcedVariant === 'b') {
    return forcedVariant;
  }
  
  // Check if user already has a variant assigned
  const storedVariant = localStorage.getItem('paywall_variant');
  if (storedVariant) {
    return storedVariant;
  }
  
  // Always assign variant 'b'
  const variant = 'b';
  localStorage.setItem('paywall_variant', variant);
  return variant;
};



const ChooseVoice = observer((props) => {
  const [quality, setQuality] = useState('Unreal');
  const [subscriptionType, setSubscriptionType] = useState(() => {
    // Get forced variant from URL params first, then fallback to mixpanel
    const forcedVariant = queryString.parse(props.location.search).variant?.toLowerCase();
    const landingPage = mixpanel.get_property('utm_landingpage');
    const isVariantB = forcedVariant === 'b' || landingPage === 'landing_b';
    if (isVariantB) return 'pro-yearly-discount';
    return pricingStore.defaultSubscriptionType;
  });
  
  // Handle auto-applied coupons and referrals
  const [autoAppliedCoupon, setAutoAppliedCoupon] = useState(null);
  const [referralUuid, setReferralUuid] = useState(null);
  const [referralConfig, setReferralConfig] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const params = queryString.parse(props.location.search);
  const app = params.app || '';
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showPaymentElement, setShowPaymentElement] = useState(false);
  
  // Debug effect to track modal state changes
  useEffect(() => {
    console.log('ChooseVoice: showPaymentElement changed to', showPaymentElement);
  }, [showPaymentElement]);
  const [paymentKey, setPaymentKey] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [setupIntentId, setSetupIntentId] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [landingVariant, setLandingVariant] = useState('A');
  const [customToken, setCustomToken] = useState(params.custom_token);
  const [device, setDevice] = useState(null);
  const [signedInWithCustomToken, setSignedInWithCustomToken] = useState(false);
  const [paywallVariant, setPaywallVariant] = useState(null);
  const history = useHistory();

  // Debug effect to track coupon state changes
  useEffect(() => {
    console.log('ChooseVoice: autoAppliedCoupon changed to', autoAppliedCoupon);
    if (autoAppliedCoupon) {
      const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
      console.log('ChooseVoice: Coupon display info for', autoAppliedCoupon, ':', couponInfo);
    }
  }, [autoAppliedCoupon]);

  // Helper function to estimate audio duration
  const estimateAudioDuration = (charCount) => {
    return Math.round((charCount / 10000) * 10.5);
  };

  // Helper function to get coupon display text and pricing adjustments
  const getCouponDisplayInfo = (couponCode) => {
    if (!couponCode) return null;
    
    console.log('ChooseVoice: getCouponDisplayInfo called with', couponCode);
    
    // Find the coupon config
    const couponConfig = Object.values(COUPON_CONFIGS).find(config => config.couponCode === couponCode) ||
                        Object.values(PARTNER_CONFIGS).find(config => config.couponCode === couponCode) ||
                        Object.values(REFERRAL_CONFIGS).find(config => config.couponCode === couponCode);
    
    console.log('ChooseVoice: Found coupon config', couponConfig);
    
    if (!couponConfig) return null;
    
    const { couponDetails } = couponConfig;
    console.log('ChooseVoice: Coupon details', couponDetails);
    
    if (couponDetails.percent_off === 100) {
      // 100% off = free month
      return {
        type: 'free_month',
        percentOff: couponDetails.percent_off,
        duration: couponDetails.duration,
        durationInMonths: couponDetails.duration_in_months,
        displayText: '1 month free',
        description: couponDetails.description
      };
    } else if (couponDetails.free_month || (couponDetails.amount_off && couponDetails.amount_off >= 999)) {
      // Amount off that covers monthly cost = free month
      console.log('ChooseVoice: Treating as free month coupon');
      return {
        type: 'free_month',
        amountOff: couponDetails.amount_off,
        currency: couponDetails.currency || 'usd',
        duration: couponDetails.duration,
        durationInMonths: couponDetails.duration_in_months,
        displayText: '1 month free',
        description: couponDetails.description
      };
    } else if (couponDetails.percent_off) {
      // Percentage discount
      return {
        type: 'percentage',
        percentOff: couponDetails.percent_off,
        duration: couponDetails.duration,
        durationInMonths: couponDetails.duration_in_months,
        displayText: `${couponDetails.percent_off}% off`,
        description: couponDetails.description
      };
    } else if (couponDetails.amount_off) {
      // Fixed amount discount
      return {
        type: 'amount',
        amountOff: couponDetails.amount_off,
        currency: couponDetails.currency || 'usd',
        duration: couponDetails.duration,
        durationInMonths: couponDetails.duration_in_months,
        displayText: `${(couponDetails.currency || 'usd').toUpperCase()} ${(couponDetails.amount_off / 100).toFixed(2)} off`,
        description: couponDetails.description
      };
    }
    
    return null;
  };

  // Helper function to calculate discounted prices
  const getDiscountedPrice = (originalPrice, couponInfo, isYearly = false) => {
    if (!couponInfo) return originalPrice;
    
    if (couponInfo.type === 'percentage') {
      const discount = originalPrice * (couponInfo.percentOff / 100);
      return originalPrice - discount;
    } else if (couponInfo.type === 'free_month') {
      // For free month coupons, return 0 for the first month
      return 0;
    } else if (couponInfo.type === 'amount') {
      // For amount off coupons, subtract the amount (convert from cents)
      const amountOff = couponInfo.amountOff / 100;
      return Math.max(0, originalPrice - amountOff);
    }
    
    return originalPrice;
  };

  useEffect(() => {
    if (!userStore.user) {
      setShouldRedirect(true);
      return;
    }
    if (userStore.user && userStore.user.vip_id) {
      setShouldRedirect(true);
    }

    // Check for auto-applied coupons from URL params or localStorage
    const urlCoupon = params.coupon;
    const urlReferral = params.referral;
    
    console.log('ChooseVoice: Checking for coupons/referrals', { urlCoupon, urlReferral });
    
    if (urlCoupon) {
      console.log('ChooseVoice: Found URL coupon', urlCoupon);
      setAutoAppliedCoupon(urlCoupon);
      setCouponCode(urlCoupon);
      // Auto-validate the coupon
      handleCouponSubmit(urlCoupon);
    } else if (urlReferral) {
      console.log('ChooseVoice: Found URL referral', urlReferral);
      setReferralUuid(urlReferral);
      // Set referral coupon
      setCouponCode('REFERRAL');
      setAutoAppliedCoupon('REFERRAL');
      // Auto-validate the coupon
      handleCouponSubmit('REFERRAL');
    } else {
      // Check localStorage for pending coupons/referrals
      const pendingCoupon = localStorage.getItem('pendingCoupon');
      const pendingReferral = localStorage.getItem('pendingReferral');
      
      console.log('ChooseVoice: Checking localStorage', { pendingCoupon, pendingReferral });
      
      if (pendingCoupon) {
        try {
          const couponData = JSON.parse(pendingCoupon);
          console.log('ChooseVoice: Parsed pending coupon', couponData);
          
          // Check if this is a partner coupon
          if (couponData.config && couponData.config.type === 'partner') {
            console.log('ChooseVoice: Applying partner coupon', couponData.code);
            setAutoAppliedCoupon(couponData.code);
            setCouponCode(couponData.code);
            // For partner coupons, we don't need to validate since we have the config
            // Just clear localStorage and continue
            localStorage.removeItem('pendingCoupon');
          } else {
            // Regular coupon, validate it
            setAutoAppliedCoupon(couponData.code);
            setCouponCode(couponData.code);
            handleCouponSubmit(couponData.code);
            localStorage.removeItem('pendingCoupon');
          }
        } catch (error) {
          console.error('Error parsing pending coupon:', error);
        }
      } else if (pendingReferral) {
        try {
          const referralData = JSON.parse(pendingReferral);
          console.log('ChooseVoice: Parsed pending referral', referralData);
          setReferralUuid(referralData.uuid);
          setReferralConfig(referralData.config);
          setCouponCode('REFERRAL');
          setAutoAppliedCoupon('REFERRAL');
          handleCouponSubmit('REFERRAL');
          localStorage.removeItem('pendingReferral');
        } catch (error) {
          console.error('Error parsing pending referral:', error);
        }
      }
    }

    // Initialize A/B test
    const variant = getPaywallVariant(params);
    setPaywallVariant(variant);
    
    // Track paywall variant visit
    mixpanel.track('paywall page a / b visited', {
      variant: variant,
      forced: !!params.paywall
    });

    // Check for forced variant in URL params first, then fallback to mixpanel
    const forcedVariant = params.variant?.toLowerCase();
    if (forcedVariant === 'a' || forcedVariant === 'b') {
      setLandingVariant(forcedVariant.toUpperCase());
      if (forcedVariant === 'b') {
        setSubscriptionType('pro-yearly-discount');
      }
    } else {
      // Get landing page variant from mixpanel
      const landingPage = mixpanel.get_property('utm_landingpage');
      console.log('landingPage', landingPage);
      if (landingPage === 'landing_b') {
        setLandingVariant('A');
        setSubscriptionType('pro-yearly-discount');
      }
    }

    // Track pricing display variant
    mixpanel.people.set({
      checkoutPricing: pricingStore.showOnlyPro ? 'pro_only' : 'all_plans'
    });
    mixpanel.track('Checkout Pricing Variant Shown', {
      variant: pricingStore.showOnlyPro ? 'pro_only' : 'all_plans'
    });

    // Simulate initial loading
    setTimeout(() => {
      setIsInitialLoading(false);
    }, 1500);
  }, [userStore.user, params.variant, params.paywall]);

  // Signup tracking useEffect
  useEffect(() => {
    if (customToken && !userStore.isSignedIn) {
      firebase.auth().signInWithCustomToken(customToken)
        .then((userCredential) => {
          // Signed in
          var user = userCredential.user;
          setSignedInWithCustomToken(true);
        })
    } else if (!customToken && userStore.isSignedIn === false && userStore.initialized){
      history.push('/signin?redirected_from=onboarding/choosevoice');
    }
  }, [customToken, userStore.isSignedIn, userStore.initialized]);

  // Device detection useEffect
  useEffect(() => {
    if (isIOS){
      setDevice('ios')
    } else if (isAndroid){
      setDevice('android')
    } else {
      setDevice('desktop')
    }
  },[]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (showPaymentElement) {
      setShowPaymentElement(true);
    }
  }, [showPaymentElement]);

  const handleCouponSubmit = async (code) => {
    if (!code.trim()) return;
    
    // Check if this is a partner coupon we already know about
    const partnerCoupon = Object.values(PARTNER_CONFIGS).find(config => config.couponCode === code.trim());
    if (partnerCoupon) {
      console.log('ChooseVoice: Partner coupon detected, skipping validation', code);
      setCouponSuccess('Partner discount applied!');
      setCouponCode(code);
      // If payment element is already shown, force reload with new setup intent to apply coupon
      if (showPaymentElement) {
        setPaymentKey(prev => prev + 1);
      }
      return;
    }
    
    setIsLoading(true);
    try {
      const result = await validateCoupon(userStore.idToken, code.trim());
      if (result.error) {
        setCouponError(result.error);
      } else if (result.valid && result.coupon_details) {
        setCouponSuccess(formatCouponMessage(result.coupon_details));
        setCouponCode(code);
        // If payment element is already shown, force reload with new setup intent to apply coupon
        if (showPaymentElement) {
          setPaymentKey(prev => prev + 1);
        }
      } else {
        setCouponError('Invalid coupon code');
      }
    } catch (error) {
      setCouponError('Failed to validate coupon');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCouponMessage = (couponDetails) => {
    let message = '';
    if (couponDetails.percent_off) {
      message = `${couponDetails.percent_off}% off`;
    } else if (couponDetails.amount_off) {
      const amount = (couponDetails.amount_off / 100).toFixed(2);
      const currency = couponDetails.currency.toUpperCase();
      message = `${currency} ${amount} off`;
    }

    if (couponDetails.duration === 'repeating' && couponDetails.duration_in_months) {
      message += ` for ${couponDetails.duration_in_months} months`;
    } else if (couponDetails.duration === 'once') {
      message += ` (one-time)`;
    } else if (couponDetails.duration === 'forever') {
      message += ` forever`;
    }

    if (couponDetails.restrictions?.minimum_amount) {
      const minAmount = (couponDetails.restrictions.minimum_amount / 100).toFixed(2);
      const currency = couponDetails.restrictions.minimum_amount_currency.toUpperCase();
      message += ` (min. ${currency} ${minAmount})`;
    }

    return message || 'Coupon applied successfully!';
  };

  const handlePaymentDetails = () => {
    setPaymentKey(prev => prev + 1);
    setShowPaymentElement(true);
  };

  const handleChangePlan = async () => {
    if (setupIntentId) {
      setIsLoading(true);
      try {
        await cancelSetupIntent(userStore.idToken, setupIntentId);
        setSetupIntentId(null);
        setShowPaymentElement(false);
        localStorage.removeItem('lastSetupIntentId');
        setPaymentKey(prev => prev + 1);
      } catch (error) {
        console.error('Error canceling setup intent:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSubscriptionTypeChange = (newType) => {
    setSubscriptionType(newType);
    
    // The CheckoutForm will automatically handle refreshing the setup intent
    // when the planType prop changes - no need to force a re-render
  };

  if (shouldRedirect) {
    const searchParams = new URLSearchParams();
    if (app) searchParams.set('app', app);
    if (params.variant) searchParams.set('variant', params.variant);
    const queryString = searchParams.toString();
    return <Redirect to={userStore.user ? `quickstart?${queryString}` : `/signin?redirected_from=signup&${queryString}`} />;
  }

  if (isInitialLoading) {
    return <Loader />;
  }

  const firstName = userStore.user?.displayName?.split(' ')[0];
  const welcomeMessage = firstName ? `👋 Hi ${firstName},\ntry a premium plan free for 7 days` : 'Start your free trial';

  // Variant B content - Speechify-inspired layout
  if (paywallVariant === 'b') {
    return (
      <div className="center-static" style={{ 
        minHeight: '100vh',
        width: '100%',
        maxWidth: '1200px',
        padding: window.innerWidth < 768 ? '20px' : '40px',
        boxSizing: 'border-box',
        overflow: 'auto',
        marginTop: window.innerWidth < 768 ? '-80px' : '-100px',
        margin: '0 auto'
      }}>
        {/* Header with Logo */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: window.innerWidth < 768 ? '20px' : '40px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: window.innerWidth < 768 ? '8px' : '12px'
          }}>
            <img 
              src="/logo192.png" 
              alt="Audioread" 
              style={{
                width: window.innerWidth < 768 ? '40px' : '60px',
                height: window.innerWidth < 768 ? '40px' : '60px'
              }}
            />
            <div style={{
              fontSize: window.innerWidth < 768 ? '20px' : '24px',
              fontWeight: 'bold',
              color: '#fff',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
              Audioread
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div style={{
          display: 'flex',
          gap: '40px',
          alignItems: 'flex-start',
          flexWrap: 'wrap'
        }}>
          {/* Left Column - Main Offer */}
          <div style={{ flex: 1.3, minWidth: '300px', textAlign: 'left', alignItems: 'flex-start', display: 'flex', flexDirection: 'column' }}>
            {/* Headline and Pricing */}
            <div style={{ marginBottom: '30px' }}>
              <h1 style={{ 
                fontSize: '36px',
                fontWeight: 'bold',
                color: '#fff',
                marginBottom: '8px'
              }}>
                Try Audioread Premium
              </h1>
              <div style={{ 
                fontSize: '20px', 
                color: '#4ade80', 
                fontWeight: 'bold',
                marginBottom: '8px'
              }}>
                {(() => {
                  const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
                  if (!couponInfo) return 'free for 7 days';
                  
                  if (couponInfo.type === 'percentage') {
                    return `free for 7 days + ${couponInfo.percentOff}% off`;
                  } else {
                    return 'free for 7 days + 1 month';
                  }
                })()}
              </div>
              <div style={{ 
                fontSize: '16px', 
                color: '#9ca3af',
                marginBottom: '10px'
              }}>
                {(() => {
                  const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
                  if (!couponInfo) return 'Then $69.00/year, only $1.35/week';
                  
                  if (couponInfo.type === 'percentage') {
                    const discountedYearly = getDiscountedPrice(69, couponInfo);
                    const discountedWeekly = discountedYearly / 52;
                    return (
                      <>
                        <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>Then $69.00/year, only $1.35/week</span>
                        <br />
                        <span style={{ color: '#4ade80' }}>Then ${discountedYearly.toFixed(2)}/year, only ${discountedWeekly.toFixed(2)}/week</span>
                      </>
                    );
                  } else {
                    return 'Then $69.00/year, only $1.35/week';
                  }
                })()}
              </div>
            </div>

            {/* Features List */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '12px',
                fontSize: '16px'
              }}>
                <span style={{ color: '#4ade80', marginRight: '12px', fontSize: '18px' }}>✓</span>
                <span style={{ color: '#fff' }}>Convert any link, file, email, text or rss feed into 100+ human voices instantly</span>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '12px',
                fontSize: '16px'
              }}>
                <span style={{ color: '#4ade80', marginRight: '12px', fontSize: '18px' }}>✓</span>
                <span style={{ color: '#fff' }}>Listen on your podcast player or any browser - No app needed!</span>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '12px',
                fontSize: '16px'
              }}>
                <span style={{ color: '#4ade80', marginRight: '12px', fontSize: '18px' }}>✓</span>
                <span style={{ color: '#fff' }}>Adjustable playback speed</span>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '12px',
                fontSize: '16px'
              }}>
                <span style={{ color: '#4ade80', marginRight: '12px', fontSize: '18px' }}>✓</span>
                <span style={{ color: '#fff' }}>80+ languages - <a href="https://help.audioread.com/en/articles/9854895-can-audioread-handle-non-english-languages" target="_blank" rel="noopener noreferrer">See full list</a></span>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '12px',
                fontSize: '16px'
              }}>
                <span style={{ color: '#4ade80', marginRight: '12px', fontSize: '18px' }}>✓</span>
                <span style={{ color: '#fff' }}>Download your audio</span>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '12px',
                fontSize: '16px'
              }}>
                <span style={{ color: '#4ade80', marginRight: '12px', fontSize: '18px' }}>✓</span>
                <span style={{ color: '#fff' }}>Get AI summaries of your content</span>
              </div>
              <div style={{ 
                display: 'flex',
                alignItems: 'center', 
                marginBottom: '12px',
                fontSize: '16px'
              }}>
                <span style={{ color: '#4ade80', marginRight: '12px', fontSize: '18px' }}>✓</span>
                <span style={{ color: '#fff' }}>Human support</span>
              </div>
            </div>

            {/* Auto-applied Coupon Notice */}
            {autoAppliedCoupon && (
              <div style={{ 
                width: '100%',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px'
              }}>
                <div style={{ 
                  color: '#4ade80', 
                  fontSize: '16px',
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: 'rgba(74, 222, 128, 0.1)',
                  borderRadius: '8px',
                  border: '2px solid rgba(74, 222, 128, 0.3)',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  🎉 Special Offer Applied: {autoAppliedCoupon}
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: 'normal', 
                    marginTop: '8px',
                    opacity: 0.9
                  }}>
                    {(() => {
                      const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
                      if (!couponInfo) return 'Your discount will be applied at checkout';
                      
                      if (couponInfo.type === 'percentage') {
                        return `${couponInfo.percentOff}% off your first payment`;
                      } else {
                        return 'FREE for 7 days + 1 month!';
                      }
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Start for Free Button */}
            <div style={{ 
              width: '100%',
              backgroundColor: 'rgba(24,15,34,0.5)',
              borderRadius: '8px'
            }}>
              <button
                onClick={() => {
                  // Set subscription type to pro-yearly-discount for the trial
                  setSubscriptionType('pro-yearly-discount');
                  setPaymentKey(prev => prev + 1);
                  setShowPaymentElement(true);
                }}
                style={{
                  width: '100%',
                  padding: '20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                }}
              >
                Start for Free
              </button>
              {/* Social Proof - Started Premium */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '20px',
              marginTop: '20px',
              marginLeft: '20px',
              gap: '8px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#fbbf24',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px'
                }}>😊</div>
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#a855f7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px'
                }}>😄</div>
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px'
                }}>😃</div>
              </div>
              <span style={{ color: '#9ca3af', fontSize: '14px' }}>3,781 started Premium this week</span>
            </div>
              
              {/* Payment Element Modal */}
              {showPaymentElement && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                  padding: '20px',
                  overflow: 'auto'
                }}>
                  <div style={{
                    backgroundColor: '#000',
                    borderRadius: '16px',
                    padding: window.innerWidth < 768 ? '20px' : '30px',
                    maxWidth: '500px',
                    width: '100%',
                    maxHeight: '90vh',
                    border: '1px solid rgba(255,255,255,0.1)',
                    position: 'relative',
                    overflow: 'auto',
                    WebkitOverflowScrolling: 'touch'
                  }}>
                    {/* Close Button */}
                    <button
                      onClick={() => {
                        setShowPaymentElement(false);
                        setPaymentKey(prev => prev + 1);
                      }}
                      style={{
                        position: 'sticky',
                        top: '0',
                        right: '20px',
                        background: 'none',
                        border: 'none',
                        color: '#9ca3af',
                        fontSize: '24px',
                        cursor: 'pointer',
                        padding: '0',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        float: 'right',
                        marginBottom: '10px',
                        zIndex: 10
                      }}
                    >
                      ×
                    </button>
                    
                    {/* Modal Header */}
                    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                      <p style={{ 
                        color: '#9ca3af', 
                        fontSize: '16px',
                        marginBottom: '20px'
                      }}>
                        Choose your billing cycle and enter payment details to begin your risk-free trial.
                      </p>
                    </div>

                    {/* Billing Selection */}
                    <div style={{ 
                      display: 'flex', 
                      gap: window.innerWidth < 768 ? '8px' : '12px', 
                      marginBottom: '30px',
                      flexDirection: window.innerWidth < 768 ? 'column' : 'row'
                    }}>
                      {/* Monthly Option */}
                      <div 
                        onClick={() => handleSubscriptionTypeChange('pro-monthly')}
                        style={{
                          flex: 1,
                          padding: window.innerWidth < 768 ? '12px' : '16px',
                          backgroundColor: subscriptionType === 'pro-monthly' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(24,15,34,0.5)',
                          border: subscriptionType === 'pro-monthly' ? '2px solid #3b82f6' : '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{ 
                          fontSize: '18px', 
                          fontWeight: 'bold', 
                          color: '#fff',
                          marginBottom: '4px'
                        }}>
                          Monthly
                        </div>
                        <div style={{ 
                          fontSize: '14px', 
                          color: '#9ca3af'
                        }}>
                          {(() => {
                            const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
                            if (!couponInfo) return '$9.99/month';
                            
                            if (couponInfo.type === 'percentage') {
                              const discountedPrice = getDiscountedPrice(9.99, couponInfo);
                              return (
                                <>
                                  <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>$9.99</span>
                                  <span style={{ color: '#4ade80', marginLeft: '8px' }}>${discountedPrice.toFixed(2)}</span>
                                </>
                              );
                            } else if (couponInfo.type === 'free_month') {
                              return (
                                <>
                                  <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>$9.99</span>
                                  <span style={{ color: '#4ade80', marginLeft: '8px' }}>FREE</span>
                                </>
                              );
                            } else {
                              return (
                                <>
                                  <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>$9.99</span>
                                  <span style={{ color: '#4ade80', marginLeft: '8px' }}>FREE</span>
                                </>
                              );
                            }
                          })()}
                        </div>
                        {autoAppliedCoupon && (
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#4ade80',
                            marginTop: '2px'
                          }}>
                            {(() => {
                              const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
                              if (!couponInfo) return '';
                              
                              if (couponInfo.type === 'percentage') {
                                return `${couponInfo.percentOff}% off your first month!`;
                              } else {
                                return 'FREE for 7 days + 1 month!';
                              }
                            })()}
                          </div>
                        )}
                        {autoAppliedCoupon && (
                          <div style={{ 
                            fontSize: '11px', 
                            color: '#9ca3af',
                            marginTop: '2px'
                          }}>
                            {(() => {
                              const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
                              if (!couponInfo) return '';
                              
                              if (couponInfo.type === 'percentage') {
                                return 'Then $9.99/month ($2.30/week)';
                              } else if (couponInfo.type === 'free_month') {
                                return 'Then $9.99/month ($2.30/week)';
                              } else {
                                return 'Then $9.99/month ($2.30/week)';
                              }
                            })()}
                          </div>
                        )}
                      </div>

                      {/* Yearly Option */}
                      <div 
                        onClick={() => handleSubscriptionTypeChange('pro-yearly-discount')}
                        style={{
                          flex: 1,
                          padding: window.innerWidth < 768 ? '12px' : '16px',
                          backgroundColor: subscriptionType === 'pro-yearly-discount' ? 'rgba(59, 130, 246,0.2)' : 'rgba(24,15,34,0.5)',
                          border: subscriptionType === 'pro-yearly-discount' ? '2px solid #3b82f6' : '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{ 
                          fontSize: '18px', 
                          fontWeight: 'bold', 
                          color: '#fff',
                          marginBottom: '4px'
                        }}>
                          Yearly
                        </div>
                        <div style={{ 
                          fontSize: '14px', 
                          color: '#9ca3af'
                        }}>
                          {(() => {
                            const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
                            if (!couponInfo) return '$5.83/month';
                            
                            if (couponInfo.type === 'percentage') {
                              const discountedPrice = getDiscountedPrice(5.83, couponInfo);
                              return (
                                <>
                                  <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>$5.83</span>
                                  <span style={{ color: '#4ade80', marginLeft: '8px' }}>${discountedPrice.toFixed(2)}</span>
                                </>
                              );
                            } else if (couponInfo.type === 'free_month') {
                              return (
                                <>
                                  <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>$5.83</span>
                                  <span style={{ color: '#4ade80', marginLeft: '8px' }}>FREE</span>
                                </>
                              );
                            } else {
                              return (
                                <>
                                  <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>$5.83</span>
                                  <span style={{ color: '#4ade80', marginLeft: '8px' }}>FREE</span>
                                </>
                              );
                            }
                          })()}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#4ade80',
                          marginTop: '2px'
                        }}>
                          {(() => {
                            const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
                            if (!couponInfo) return 'Save 42%';
                            
                            if (couponInfo.type === 'percentage') {
                              return `${couponInfo.percentOff}% off your first year!`;
                            } else {
                              return 'FREE for 7 days + 1 month!';
                            }
                          })()}
                        </div>
                        {autoAppliedCoupon && (
                          <div style={{ 
                            fontSize: '11px', 
                            color: '#9ca3af',
                            marginTop: '2px'
                          }}>
                            {(() => {
                              const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
                              if (!couponInfo) return '';
                              
                              if (couponInfo.type === 'percentage') {
                                return 'Then $69/year ($1.35/week)';
                              } else if (couponInfo.type === 'free_month') {
                                return 'Then $69/year ($1.35/week)';
                              } else {
                                return 'Then $69/year ($1.35/week)';
                              }
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Payment Form */}
                    <CheckoutForm 
                      planType={subscriptionType}
                      key={paymentKey}
                      couponCode={couponCode}
                      autoAppliedCoupon={autoAppliedCoupon}
                      immediateSetupIntent={true}
                      onSetupIntentCreated={(setupIntentId) => {
                        setSetupIntentId(setupIntentId);
                        
                        // Track subscription setup
                        mixpanel.track('Quickstart: Subscription setup done', {
                          completed: true,
                          plan: 'pro',
                          billing: subscriptionType.includes('yearly') ? 'yearly' : 'monthly',
                          paywall: paywallVariant
                        });

                        // Handle referral completion if applicable
                        if (referralUuid) {
                          import('../../../audiblogsApi').then(({ completeReferralSignup }) => {
                            completeReferralSignup(userStore.idToken, referralUuid)
                              .then((result) => {
                                if (!result.error) {
                                  mixpanel.track('Referral Signup Completed', {
                                    referral_uuid: referralUuid,
                                    referrer_rewarded: true
                                  });
                                }
                              })
                              .catch((error) => {
                                console.error('Error completing referral:', error);
                              });
                          });
                        }
                      }}
                      onShowPaymentElement={setShowPaymentElement}
                    />
                  </div>
                </div>
              )}
            </div>


          </div>

          {/* Right Column - Social Proof Reviews */}
          <div style={{ 
            flex: 2, 
            minWidth: '300px'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '12px',
              maxWidth: '100%'
            }}>
              {/* Review Card 1 */}
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '120px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#8b5cf6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>😊</div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '12px' }}>★★★★★</span>
                </div>
                <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.3', marginBottom: '4px' }}>
                  "Perfect for grad students! Saves me hours."
                </div>
                <div style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'bold' }}>
                  Helen W
                </div>
              </div>

              {/* Review Card 2 */}
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '120px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#3b82f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>😄</div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '12px' }}>★★★★★</span>
                </div>
                <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.3', marginBottom: '4px' }}>
                  "100x better than Pocket"
                </div>
                <div style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'bold' }}>
                  Aidan Torrence
                </div>
              </div>

              {/* Review Card 3 */}
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '120px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>😃</div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '12px' }}>★★★★★</span>
                </div>
                <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.3', marginBottom: '4px' }}>
                  "My TLDR Audio button. Highly recommend!"
                </div>
                <div style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'bold' }}>
                  Arvin Garcia
                </div>
              </div>

              {/* Review Card 4 */}
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '120px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#f59e0b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>😁</div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '12px' }}>★★★★★</span>
                </div>
                <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.3', marginBottom: '4px' }}>
                  "Amazing! Voice quality comparable to Audible."
                </div>
                <div style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'bold' }}>
                  Johanna Quinn
                </div>
              </div>

              {/* Review Card 5 */}
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '120px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>🤩</div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '12px' }}>★★★★★</span>
                </div>
                <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.3', marginBottom: '4px' }}>
                  "Fucking fantastic. Great voice quality."
                </div>
                <div style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'bold' }}>
                  Brent Maxwell
                </div>
              </div>

              {/* Review Card 8 */}
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '120px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#84cc16',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>🥳</div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '12px' }}>★★★★★</span>
                </div>
                <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.3', marginBottom: '4px' }}>
                  "Amazeballs"
                </div>
                <div style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'bold' }}>
                  Geffrey van der Bos
                </div>
              </div>

              {/* Review Card 9 */}
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '120px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#ec4899',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>😍</div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '12px' }}>★★★★★</span>
                </div>
                <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.3', marginBottom: '4px' }}>
                  "Have been waiting for something like this for so long. YES"
                </div>
                <div style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'bold' }}>
                  Elianna Goldstein
                </div>
              </div>

              {/* Review Card 10 */}
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '120px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#f97316',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>🤩</div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '12px' }}>★★★★★</span>
                </div>
                <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.3', marginBottom: '4px' }}>
                  "Absolutely Amazing and will save alot of times spent on reading"
                </div>
                <div style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'bold' }}>
                  @astonish2c
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Accordion Section */}
        <div style={{
          width: '100%',
          margin: '40px auto 30px',
          padding: '20px',
          backgroundColor: 'rgba(24,15,34,0.3)',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <h3 style={{ 
            fontSize: '20px', 
            color: '#fff', 
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            Frequently Asked Questions
          </h3>
          
          {/* FAQ Item 1 */}
          <div style={{ marginBottom: '16px' }}>
            <button
              onClick={() => {
                const content = document.getElementById('faq1-content');
                content.style.display = content.style.display === 'none' ? 'block' : 'none';
              }}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 'bold',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              How does Audioread work?
              <span style={{ fontSize: '20px' }}>▼</span>
            </button>
            <div id="faq1-content" style={{
              padding: '16px',
              backgroundColor: 'rgba(24,15,34,0.3)',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              color: '#e5e7eb',
              fontSize: '14px',
              lineHeight: '1.5',
              display: 'none'
            }}>
              Simply paste any link, upload files, forward emails, or add RSS feeds - Audioread instantly converts them to high-quality audio. Listen directly in your browser or sync to your favorite podcast player for seamless offline listening.
            </div>
          </div>

          {/* FAQ Item 2 */}
          <div style={{ marginBottom: '16px' }}>
            <button
              onClick={() => {
                const content = document.getElementById('faq2-content');
                content.style.display = content.style.display === 'none' ? 'block' : 'none';
              }}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 'bold',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              How can I use Audioread?
              <span style={{ fontSize: '20px' }}>▼</span>
            </button>
            <div id="faq2-content" style={{
              padding: '16px',
              backgroundColor: 'rgba(24,15,34,0.3)',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              color: '#e5e7eb',
              fontSize: '14px',
              lineHeight: '1.5',
              display: 'none'
            }}>
              Setup takes just 5 minutes: install our browser extension, configure shortcuts, add RSS feeds, and connect your podcast app. After that, adding content is completely automatic or requires just 2 clicks - your audio appears in your podcast player instantly, ready to listen.
            </div>
          </div>

          {/* FAQ Item 3 */}
          <div style={{ marginBottom: '16px' }}>
            <button
              onClick={() => {
                const content = document.getElementById('faq3-content');
                content.style.display = content.style.display === 'none' ? 'block' : 'none';
              }}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 'bold',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              What's special about Audioread?
              <span style={{ fontSize: '20px' }}>▼</span>
            </button>
            <div id="faq3-content" style={{
              padding: '16px',
              backgroundColor: 'rgba(24,15,34,0.3)',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              color: '#e5e7eb',
              fontSize: '14px',
              lineHeight: '1.5',
              display: 'none'
            }}>
              Audioread combines all the premium features you'd expect from other services - but at the lowest cost for high-quality AI voices. Plus, we're the only service that lets you listen through your podcast player, giving you access to all your favorite podcast features like speed control, sleep timers, and offline downloads.
            </div>
          </div>

          {/* FAQ Item 4 */}
          <div style={{ marginBottom: '16px' }}>
            <button
              onClick={() => {
                const content = document.getElementById('faq4-content');
                content.style.display = content.style.display === 'none' ? 'block' : 'none';
              }}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 'bold',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              How to cancel subscription?
              <span style={{ fontSize: '20px' }}>▼</span>
            </button>
            <div id="faq4-content" style={{
              padding: '16px',
              backgroundColor: 'rgba(24,15,34,0.3)',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              color: '#e5e7eb',
              fontSize: '14px',
              lineHeight: '1.5',
              display: 'none'
            }}>
              Your subscription is completely risk-free. Cancel anytime with no questions asked - you'll keep access until the end of your current billing period. No hidden fees, no complicated cancellation process.
            </div>
          </div>
        </div>

        {/* Terms */}
        <div style={{ 
          textAlign: 'center', 
          marginTop: '30px',
          fontSize: '12px',
          color: '#666'
        }}>
          By continuing, you agree to our{' '}
          <a 
            href="/terms" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#fff', textDecoration: 'underline' }}
          >
            Terms of Service
          </a>
        </div>
      </div>
    );
  }

  // Variant A (original content)
  return (
    <div className="center-static" style={{ 
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100%',
      padding: '20px',
      boxSizing: 'border-box',
      overflow: 'auto'
    }}>
      <h1 className="headline" style={{ marginBottom: '0px', whiteSpace: 'pre-line' }}>
        {welcomeMessage}
      </h1>

      {/* Social Proof */}
      <div style={{
        textAlign: 'center',
        margin: '20px 0',
        padding: '15px',
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderRadius: '8px',
        fontSize: '14px'
      }}>
        <div style={{ marginBottom: '10px' }}>⭐️ Join 100,000+ happy listeners</div>
        <div style={{ opacity: 0.8 }}>"This app has completely changed how I consume content" - Sarah K.</div>
      </div>

      {/* Urgency */}
      {/* {!pricingStore.showOnlyPro && (
        <div style={{
          textAlign: 'center',
          margin: '20px 0',
          padding: '15px',
          backgroundColor: 'rgba(255,143,65,0.1)',
          borderRadius: '8px',
          fontSize: '14px',
          border: '1px solid rgba(255,143,65,0.3)'
        }}>
          <div style={{ color: 'rgb(255 143 65)', fontWeight: 'bold', fontSize: '16px' }}>🔥 EXCLUSIVE 1-TIME OFFER</div>
          <div style={{ marginTop: '8px', opacity: 0.9 }}>
            <div style={{ marginBottom: '4px' }}>🎁 7-day free trial + 50% off your first payment</div>
            <div style={{ fontSize: '13px', opacity: 0.8 }}>Use code <span style={{ fontWeight: 'bold', color: 'rgb(255 143 65)' }}>HALFOFF</span> - Works on both monthly & yearly plans!</div>
            <div style={{ fontSize: '12px', marginTop: '6px', fontStyle: 'italic', opacity: 0.7 }}>This offer is only available when starting your trial now. If you sign up later, you'll miss out on the 50% discount.</div>
          </div>
        </div>
      )} */}

      <h2 className="subheadline" style={{ whiteSpace: 'pre-line' }}>Choose your plan</h2>
      
      {/* Auto-applied Coupon Notice */}
      {autoAppliedCoupon && (
        <div style={{ 
          color: '#4ade80', 
          fontSize: '18px',
          margin: '20px auto',
          padding: '20px',
          backgroundColor: 'rgba(74, 222, 128, 0.1)',
          borderRadius: '12px',
          border: '2px solid rgba(74, 222, 128, 0.3)',
          textAlign: 'center',
          fontWeight: 'bold',
          maxWidth: '600px'
        }}>
          🎉 Special Offer Applied: {autoAppliedCoupon}
          <div style={{ 
            fontSize: '14px', 
            fontWeight: 'normal', 
            marginTop: '8px',
            opacity: 0.9
          }}>
            {(() => {
              const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
              if (!couponInfo) return 'Your discount will be applied at checkout';
              
              if (couponInfo.type === 'percentage') {
                return `${couponInfo.percentOff}% off your first payment`;
              } else {
                return 'FREE for 7 days + 1 month!';
              }
            })()}
          </div>
          <div style={{ 
            fontSize: '12px', 
            fontWeight: 'normal', 
            marginTop: '4px',
            opacity: 0.8
          }}>
            {(() => {
              const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
              if (!couponInfo) return 'Then $69.00/year, only $1.35/week';
              
              if (couponInfo.type === 'percentage') {
                const discountedYearly = getDiscountedPrice(69, couponInfo);
                const discountedWeekly = discountedYearly / 52;
                return (
                  <>
                    <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>Then $69.00/year, only $1.35/week</span>
                    <br />
                    <span style={{ color: '#4ade80' }}>Then ${discountedYearly.toFixed(2)}/year, only ${discountedWeekly.toFixed(2)}/week</span>
                  </>
                );
              } else {
                return 'Then $69.00/year, only $1.35/week';
              }
            })()}
          </div>
        </div>
      )}
      
      {/* Plan Selection Cards */}
      <PricingCards 
        onPlanSelect={handleSubscriptionTypeChange}
        selectedPlan={subscriptionType}
        showPaymentElement={showPaymentElement}
        estimateAudioDuration={estimateAudioDuration}
        autoAppliedCoupon={autoAppliedCoupon}
      />

      {/* Change Plan Button */}
      {showPaymentElement && (
        <div style={{ textAlign: 'center', margin: '20px 0' }}>
          <button 
            onClick={handleChangePlan}
            disabled={isLoading}
            style={{
              padding: '8px 20px',
              backgroundColor: 'transparent',
              border: '1px solid #fff',
              borderRadius: '4px',
              color: '#fff',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              width: 'fit-content',
              fontSize: '14px',
              opacity: isLoading ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              margin: '0 auto'
            }}
          >
            {isLoading ? (
              <>
                <img src={loadingIcon} alt="Loading..." style={{ width: '16px', height: '16px' }} />
                Changing Plan...
              </>
            ) : (
              'Change Plan'
            )}
          </button>
        </div>
      )}

      {/* Auto-applied Coupon Notice */}
      {autoAppliedCoupon && (
        <div style={{ 
          color: '#4ade80', 
          fontSize: '18px',
          margin: '20px auto',
          padding: '20px',
          backgroundColor: 'rgba(74, 222, 128, 0.1)',
          borderRadius: '12px',
          border: '2px solid rgba(74, 222, 128, 0.3)',
          textAlign: 'center',
          fontWeight: 'bold',
          maxWidth: '600px'
        }}>
          🎉 Special Offer Applied: {autoAppliedCoupon}
          <div style={{ 
            fontSize: '14px', 
            fontWeight: 'normal', 
            marginTop: '8px',
            opacity: 0.9
          }}>
            {(() => {
              const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
              if (!couponInfo) return 'Your discount will be applied at checkout';
              
              if (couponInfo.type === 'percentage') {
                return `${couponInfo.percentOff}% off your first payment`;
              } else {
                return 'FREE for 7 days + 1 month!';
              }
            })()}
          </div>
        </div>
      )}

      {/* Coupon and Payment Section */}
      <div style={{ 
        maxWidth: '100%', 
        margin: '20px auto',
        padding: '20px',
        backgroundColor: 'rgba(24,15,34,0.5)',
        borderRadius: '8px',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {subscriptionType === 'free' ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ maxWidth: '300px', margin: '0 auto' }}>
              <button
                onClick={() => {
                  mixpanel.track('Quickstart: Subscription setup done', {
                    completed: true,
                    plan: 'free',
                    paywall: paywallVariant
                  });
                  mixpanel.people.set_once({
                    subscription_plan: 'free',
                    registered: true
                  });
                  props.history.push('/add');
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  textAlign: 'center'
                }}
              >
                Continue to App
              </button>
            </div>
            <div style={{ 
              textAlign: 'center', 
              marginTop: '20px',
              fontSize: '12px',
              color: '#666'
            }}>
              By continuing, you agree to our{' '}
              <a 
                href="/terms" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#fff', textDecoration: 'underline' }}
              >
                Terms of Service
              </a>
            </div>
          </div>
        ) : (
          <CheckoutForm 
            planType={subscriptionType} 
            couponCode={couponCode}
            autoAppliedCoupon={autoAppliedCoupon}
            onSetupIntentCreated={(setupIntentId) => {
              setSetupIntentId(setupIntentId);
              const plan = subscriptionType.includes('pro') ? 'pro' : 'basic';
              
              // Track subscription setup
              mixpanel.track('Quickstart: Subscription setup done', {
                completed: true,
                plan: plan,
                billing: subscriptionType.includes('yearly') ? 'yearly' : 'monthly',
                paywall: paywallVariant
              });
              
            }}
            onShowPaymentElement={setShowPaymentElement}
          />
        )}
      </div>

      {/* Money Back Guarantee */}
      <div style={{
        textAlign: 'center',
        margin: '20px auto',
        maxWidth: '600px',
        padding: '15px',
        backgroundColor: 'rgba(76,175,80,0.1)',
        borderRadius: '8px',
        fontSize: '14px',
        border: '1px solid rgba(76,175,80,0.3)'
      }}>
        <div style={{ color: '#4CAF50', fontWeight: 'bold', marginBottom: '5px' }}>🔒 100% Money-Back Guarantee</div>
        <div style={{ opacity: 0.8 }}>If you're not satisfied within 30 days, we'll refund your payment. No questions asked.</div>
      </div>

      {/* FAQ Section */}
      <div style={{
        maxWidth: '600px',
        margin: '20px auto',
        padding: '20px',
        backgroundColor: 'rgba(24,15,34,0.5)',
        borderRadius: '8px'
      }}>
        <h3 style={{ marginBottom: '15px', fontSize: '18px' }}>Frequently Asked Questions</h3>
        
        <div style={{ marginBottom: '15px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>What are the article length limits?</div>
          <div style={{ opacity: 0.8, fontSize: '14px' }}>
            Free users can convert articles up to 10,000 characters (~{estimateAudioDuration(10000)} minutes of audio). 
            Paid plans (Basic and Pro) support articles up to 100,000 characters (~{estimateAudioDuration(100000)} minutes of audio).
          </div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>What happens after the 7-day trial?</div>
          <div style={{ opacity: 0.8, fontSize: '14px' }}>Your subscription will automatically continue at the regular price. You can cancel anytime before the trial ends.</div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Can I cancel anytime?</div>
          <div style={{ opacity: 0.8, fontSize: '14px' }}>Yes, you can cancel your subscription at any time. Your plan will stay active until the end of the current billing period.</div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>What payment methods do you accept?</div>
          <div style={{ opacity: 0.8, fontSize: '14px' }}>We accept all major credit cards, including Visa, Mastercard, American Express, Discover, and Apple/Google Pay.</div>
        </div>

        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Is my payment information secure?</div>
          <div style={{ opacity: 0.8, fontSize: '14px' }}>Yes, we use Stripe for secure payment processing. We never store your credit card information.</div>
        </div>
      </div>
            {/* Stick with Free Button - Moved below payment section */}
            <div
        style={{ 
          maxWidth: 300, 
          margin: '20px auto', 
          textAlign: 'center',
          opacity: 0.7,
          fontSize: '14px'
        }}
      >
        {/* <span 
          style={{ 
            cursor: 'pointer',
            textDecoration: 'underline',
            color: '#fff'
          }}
          onClick={() => {
            mixpanel.track('Quickstart: Subscription setup done - free version', {
              completed: true,
              plan: 'free'
            });
            mixpanel.people.set_once({
              subscription_plan: 'free',
              registered: true
            });
            props.history.push('/add');
          }}
        >
          Continue with limited free version
        </span> */}
      </div>
    </div>
  );
});

export default ChooseVoice;
